import { useReference } from "lib/@hooks/use-reference"
import { useState } from "react"
import { htmlToText } from "lib/html-to-text"
import { Box, Typography } from "@mui/material"
import { BoundEditableRichText } from "lib/@components/editable/editableRichText"
import { If } from "lib/@components/switch"
import { Accordion } from "minimals-template/components/accordion"
import { CollapseAllTasks, ScheduleIntroEdited } from "event-definitions"
import { useScheduleSettings } from "routes/schedule/lib/useScheduleSettings"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { useTranslationContext } from "routes/schedule/plugins/translation/use-translation-context"
import { normalizeActions } from "library/normalizeActions.mjs"
import { useDarkMode } from "lib/use-dark-mode"
import { createSlot } from "lib/@components/slot/create-slot"
import {
    ScheduleIntroductionContentAfter,
    ScheduleIntroductionTitleBottom,
    ScheduleIntroductionTitleTop,
} from "slot-definitions"
import { Bound } from "lib/@components/binding/Bound"
import { ListItemBox } from "lib/@components/ListItemBox"

export const ScheduleIntroductionContents = createSlot("ScheduleIntroduction")
export const ScheduleIntroductionContentWrapper = createSlot("ScheduleIntroductionContentWrapper")
export const ScheduleTopmost = createSlot("ScheduleTopmost")
export const ScheduleBottom = createSlot("ScheduleBottom")

export function ScheduleIntroduction({ schedule }) {
    const [reference, save] = useReference({}, "showInfo")
    const { showAll, editMode } = useBoundContext()
    const { labels: translationLabels } = useTranslationContext()
    const [{ showDetail }] = useScheduleSettings()
    const [openIntro, setOpenIntro] = useState(reference.showIntro ?? true)
    const hasContent = htmlToText((schedule?.intro?.content ?? "") + (schedule?.intro?.note ?? "")).trim().length > 0
    reference.showIntro = openIntro
    CollapseAllTasks.useEvent(() => setOpenIntro(false))
    save({ showIntro: openIntro })
    const isDark = useDarkMode()

    return (
        <>
            {(!!hasContent || editMode || showAll) && (
                <Accordion
                    data-cy="schedule-introduction"
                    alwaysOpen={showDetail || !schedule.tasks.length || showAll}
                    noCollapse
                    divider
                    title={
                        <>
                            <ListItemBox>
                                <Box
                                    data-cy="schedule-introduction-toggle"
                                    role="button"
                                    sx={{ cursor: "pointer", py: 0.5 }}
                                >
                                    <strong>{translationLabels?.introduction ?? "Introduction"}</strong>
                                    <Box flex={1} />
                                </Box>
                                <Box flex={1} />
                                <ScheduleIntroductionTitleTop.Slot item={{ taskId: "intro" }} />
                            </ListItemBox>

                            <Bound schedule={schedule}>
                                <ScheduleIntroductionTitleBottom.Slot />
                            </Bound>
                        </>
                    }
                    open={openIntro}
                    onChange={(v) => {
                        setOpenIntro(v)
                        save({ showIntro: v })
                    }}
                >
                    <ScheduleIntroductionContents.Slot>
                        <ScheduleIntroductionContentWrapper.Wrapper schedule={schedule}>
                            <Box mt={2} mb={3}>
                                <Box data-cy="schedule-intro-content">
                                    <BoundEditableRichText
                                        field="intro.content"
                                        transformIn={normalizeActions}
                                        onChanged={() => ScheduleIntroEdited.raise()}
                                    />
                                </Box>
                                <ScheduleIntroductionContentAfter.Slot />
                                <If truthy={htmlToText(schedule.intro?.note).trim().length || editMode}>
                                    <Box
                                        data-cy="schedule-intro-note"
                                        sx={{
                                            mt: 2,
                                            paddingInlineStart: 2,
                                            paddingInlineEnd: { lg: 16, md: 4, xs: 0 },
                                            fontSize: 14,
                                        }}
                                    >
                                        <Typography variant="overline">
                                            {translationLabels?.notes ?? "Notes"}
                                        </Typography>
                                        <Box sx={{ color: isDark ? "#aaa" : "#333" }}>
                                            <BoundEditableRichText
                                                label="Notes"
                                                field="intro.note"
                                                onChanged={() => ScheduleIntroEdited.raise()}
                                            />
                                        </Box>
                                    </Box>
                                </If>
                            </Box>
                        </ScheduleIntroductionContentWrapper.Wrapper>
                    </ScheduleIntroductionContents.Slot>
                </Accordion>
            )}
        </>
    )
}
